import type {ConnectSource} from '@smartmixin/core/src/utils/Analytics/schema';
import {useLocation} from 'react-router';

/**
 * Where the user set off to connect a service, for the GA connect events
 * (#379, 4c): carried in router state, never in the URL, so page views keep
 * one path per page and a shared or bookmarked link carries nothing. Each
 * entry link sets it (the shell's "Add a station": `drawer`, Settings >
 * Weather Services: `settings`, the stations empty state: `home`) and the
 * pages between it and the connect form hand it on. A page reached any
 * other way (typed URL, Help Centre link, new tab) has none: `other`.
 */
export type ConnectSourceState = {connectSource: ConnectSource};

export const connectSourceState = (
  connectSource: ConnectSource,
): ConnectSourceState => ({connectSource});

/** This page's connect source, if the link that opened it set one. */
export const useConnectSource = (): ConnectSource | undefined => {
  const state: unknown = useLocation().state;
  const value =
    state !== null && typeof state === 'object'
      ? (state as {connectSource?: unknown}).connectSource
      : undefined;
  // Allowlisted when sent (core's connectParams); a string is all it takes.
  return typeof value === 'string' ? (value as ConnectSource) : undefined;
};

/** The router state to hand on: this page's connect source, if any. */
export const useConnectSourceState = (): ConnectSourceState | undefined => {
  const source = useConnectSource();
  return source ? connectSourceState(source) : undefined;
};

/**
 * A first connect, or a reconnect of an account on hold. Only a first
 * connect reports (as on mobile); without an intent nothing does.
 */
export type ConnectIntent = 'connect' | 'reconnect';

/** What a provider page hands its connect form. */
export type ConnectProps = {
  title: string;
  intent?: ConnectIntent;
  source?: ConnectSource;
};
